import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, inject, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ThematicBlockRead } from '@isophi/mng-api';
import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable } from 'rxjs';

import { ThematicBlockStoreService } from '../../services/thematic-block-store/thematic-block-store.service';

/**
 * Tematický celek picker (value = block id or null). Searchable; typing a new name offers to create it.
 * Archived blocks are hidden unless they are the current value.
 */
@Component({
  selector: 'app-thematic-block-select',
  templateUrl: './thematic-block-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ThematicBlockSelectComponent), multi: true }],
})
export class ThematicBlockSelectComponent implements ControlValueAccessor {
  @Input() placeholder = '';
  @Input() clearable = true;
  @Input() invalid = false;
  @Input() labelForId: string | null = null;

  private readonly store = inject(ThematicBlockStoreService);
  private readonly cdr = inject(ChangeDetectorRef);

  protected value: string | null = null;
  protected disabled = false;
  private readonly value$ = new BehaviorSubject<string | null>(null);

  protected readonly items$: Observable<ThematicBlockRead[]> = combineLatest([this.store.blocks$, this.value$]).pipe(
    map(([blocks, value]) => blocks.filter((b) => !b.archived || b.id === value))
  );

  private onChange: (value: string | null) => void = () => undefined;
  protected onTouched: () => void = () => undefined;

  /** ng-select addTag: create the block on the server and select it. */
  protected readonly addTag = (title: string): Promise<ThematicBlockRead> => firstValueFrom(this.store.create(title.trim()));

  writeValue(value: string | null): void {
    this.value = value ?? null;
    this.value$.next(this.value);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.cdr.markForCheck();
  }

  protected select(block: ThematicBlockRead | null): void {
    this.value = block?.id ?? null;
    this.value$.next(this.value);
    this.onChange(this.value);
  }
}
